// =====================================================
// COMPOSANT: AppelsSection — « Suivi des appels par équipage » (RUN 5)
// =====================================================
// Tableau des buckets d'appels (0 / 1 / 2 / 3+) × (BPI/PR | PI | Total). Le
// bucket « 0 appel » (LE besoin de Maxime : cibles de relance jamais appelées)
// est mis en avant. Lignes DÉPLIABLES → dropdown : détail des relances
// (date/canal/commercial/statut) pour 1/2/3+, liste des cibles pour « 0 appel ».
// PAS de vue par-user (déjà dans la timeline). Design system crm-4l (zinc).

import { useState } from 'react';
import { ChevronDown, ChevronRight, PhoneOff, PhoneCall } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { CALL_DIRECTION_LABELS, CALL_OUTCOME_LABELS } from '@/types/timeline';
import type { AppelsData, CallBucket, CallDetail, UncalledEntity } from '@/lib/dashboard-stats';

/** Nombre max d'items listés dans un dropdown (garde-fou volume). */
const DETAIL_CAP = 60;

/** Badge de segment (BPI/PR ou PI). */
function SegmentBadge({ segment }: { segment: 'bpi_pr' | 'pi' }) {
  const isPi = segment === 'pi';
  return (
    <span
      className={[
        'inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
        isPi ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-700',
      ].join(' ')}
    >
      {isPi ? 'PI' : 'BPI/PR'}
    </span>
  );
}

/** Badge de statut d'appel (outcome). */
function OutcomeBadge({ outcome }: { outcome: CallDetail['outcome'] }) {
  if (!outcome) return <span className="text-zinc-400">—</span>;
  const styles: Record<string, string> = {
    answered: 'bg-green-50 text-green-700',
    voicemail: 'bg-amber-50 text-amber-700',
    no_answer: 'bg-red-50 text-red-700',
  };
  return (
    <span
      className={[
        'inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium',
        styles[outcome] ?? 'bg-zinc-100 text-zinc-600',
      ].join(' ')}
    >
      {CALL_OUTCOME_LABELS[outcome]}
    </span>
  );
}

/** Détail des relances d'un bucket ≥ 1 : date / canal / commercial / statut. */
function CallsDetail({ calls }: { calls: CallDetail[] }) {
  const shown = calls.slice(0, DETAIL_CAP);
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-zinc-500">
            <th className="py-1.5 pl-1 pr-3 text-left font-semibold">Équipage / prospect</th>
            <th className="py-1.5 pr-3 text-left font-semibold">Date</th>
            <th className="py-1.5 pr-3 text-left font-semibold">Canal</th>
            <th className="py-1.5 pr-3 text-left font-semibold">Commercial</th>
            <th className="py-1.5 pr-1 text-left font-semibold">Statut</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((c) => (
            <tr key={c.id} className="border-t border-zinc-100">
              <td className="py-1.5 pl-1 pr-3">
                <div className="flex items-center gap-1.5">
                  <SegmentBadge segment={c.segment} />
                  <span className="text-zinc-800">{c.entityLabel}</span>
                </div>
              </td>
              <td className="py-1.5 pr-3 tabular-nums text-zinc-600">
                {new Date(c.date).toLocaleDateString('fr-FR')}
              </td>
              <td className="py-1.5 pr-3 text-zinc-600">
                {c.direction ? CALL_DIRECTION_LABELS[c.direction] : '—'}
              </td>
              <td className="py-1.5 pr-3 text-zinc-600">{c.commercialName ?? '—'}</td>
              <td className="py-1.5 pr-1">
                <OutcomeBadge outcome={c.outcome} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {calls.length > DETAIL_CAP ? (
        <p className="mt-2 pl-1 text-[11px] text-zinc-400">
          {shown.length} relances affichées sur {calls.length.toLocaleString('fr-FR')}.
        </p>
      ) : null}
    </div>
  );
}

/** Liste des cibles jamais appelées (bucket « 0 appel »). */
function UncalledDetail({ entities }: { entities: UncalledEntity[] }) {
  const shown = entities.slice(0, DETAIL_CAP);
  return (
    <div>
      <p className="mb-2 pl-1 text-[11px] font-medium text-amber-700">
        Cibles jamais appelées — à programmer dans les vagues de relance.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((e) => (
          <span
            key={e.id}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2 py-1 text-xs text-zinc-700"
          >
            <SegmentBadge segment={e.segment} />
            {e.label}
          </span>
        ))}
      </div>
      {entities.length > DETAIL_CAP ? (
        <p className="mt-2 pl-1 text-[11px] text-zinc-400">
          {shown.length} cibles affichées sur {entities.length.toLocaleString('fr-FR')}.
        </p>
      ) : null}
    </div>
  );
}

function BucketRow({ bucket }: { bucket: CallBucket }) {
  const [open, setOpen] = useState(false);
  const isZero = bucket.key === '0';
  const canExpand = bucket.total > 0;

  return (
    <>
      <tr
        className={[
          'border-t border-zinc-100 transition-colors',
          canExpand ? 'cursor-pointer hover:bg-zinc-50' : '',
          isZero ? 'bg-amber-50/40' : '',
        ].join(' ')}
        onClick={canExpand ? () => setOpen((o) => !o) : undefined}
      >
        <td className="py-2.5 pl-3 pr-2">
          <div className="flex items-center gap-2">
            {canExpand ? (
              open ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-zinc-400" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-zinc-400" />
              )
            ) : (
              <span className="inline-block h-4 w-4 shrink-0" />
            )}
            {isZero ? (
              <PhoneOff className="h-4 w-4 shrink-0 text-amber-500" />
            ) : (
              <PhoneCall className="h-4 w-4 shrink-0 text-zinc-400" />
            )}
            <span
              className={['text-sm font-medium', isZero ? 'text-amber-800' : 'text-zinc-900'].join(
                ' '
              )}
            >
              {bucket.label}
            </span>
          </div>
        </td>
        <td className="py-2.5 pr-3 text-right text-sm tabular-nums text-zinc-700">
          {bucket.bpiPr.toLocaleString('fr-FR')}
        </td>
        <td className="py-2.5 pr-3 text-right text-sm tabular-nums text-zinc-700">
          {bucket.pi.toLocaleString('fr-FR')}
        </td>
        <td className="py-2.5 pr-3 text-right text-sm font-semibold tabular-nums text-zinc-900">
          {bucket.total.toLocaleString('fr-FR')}
        </td>
      </tr>
      {open && canExpand ? (
        <tr className="border-t border-zinc-100 bg-zinc-50/60">
          <td colSpan={4} className="px-3 py-3">
            {isZero ? (
              <UncalledDetail entities={bucket.uncalled} />
            ) : (
              <CallsDetail calls={bucket.calls} />
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}

interface AppelsSectionProps {
  appels: AppelsData;
}

export function AppelsSection({ appels }: AppelsSectionProps) {
  const { buckets, totalEntities, totalCalls, callsOutsideUniverse } = appels;
  const zero = buckets.find((b) => b.key === '0');

  return (
    <section className="flex flex-col gap-6">
      {/* Titre de section */}
      <div className="flex items-baseline gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-700">
          Suivi des appels
        </h2>
        <div className="h-px flex-1 bg-zinc-200" />
      </div>

      <Card className="border border-zinc-200">
        <CardContent className="flex flex-col gap-4 px-6">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex flex-col">
              <h3 className="text-sm font-semibold text-zinc-950">
                Nombre d'appels par équipage / prospect
              </h3>
              <span className="text-xs text-zinc-500">
                Pour programmer les vagues de relance · cliquer une ligne → détail
              </span>
            </div>
            {zero && zero.total > 0 ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                <PhoneOff className="h-3.5 w-3.5" />
                {zero.total.toLocaleString('fr-FR')} jamais appelés
              </span>
            ) : null}
          </div>

          <div className="overflow-hidden rounded-xl border border-zinc-200">
            <table className="w-full border-collapse">
              <thead className="bg-zinc-50">
                <tr className="text-[11px] uppercase tracking-wide text-zinc-500">
                  <th className="py-2 pl-3 pr-2 text-left font-semibold">Nb d'appels</th>
                  <th className="py-2 pr-3 text-right font-semibold">BPI/PR</th>
                  <th className="py-2 pr-3 text-right font-semibold">PI</th>
                  <th className="py-2 pr-3 text-right font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((b) => (
                  <BucketRow key={b.key} bucket={b} />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-zinc-200 bg-zinc-50 text-sm font-semibold text-zinc-900">
                  <td className="py-2 pl-3 pr-2">Total relançables</td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {buckets.reduce((s, b) => s + b.bpiPr, 0).toLocaleString('fr-FR')}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {buckets.reduce((s, b) => s + b.pi, 0).toLocaleString('fr-FR')}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {totalEntities.toLocaleString('fr-FR')}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Note d'honnêteté : périmètre + données seed */}
          <p className="text-xs leading-relaxed text-zinc-500">
            Unité = contact (les appels sont consignés sur le contact).{' '}
            <span className="font-medium text-zinc-600">BPI/PR</span> = prospects + contacts membres
            d'un équipage « BPI payé, pré-inscription en attente ».{' '}
            <span className="font-medium text-zinc-600">PI</span> = contacts membres d'un équipage
            pré-inscrit (pas encore inscrit définitif). Table de relance : inscrits définitifs,
            résiliés et reportés sont exclus (aucune relance nécessaire). Appels comptés en cumul (
            {totalCalls.toLocaleString('fr-FR')} appels rattachés
            {callsOutsideUniverse > 0
              ? ` ; ${callsOutsideUniverse.toLocaleString('fr-FR')} hors périmètre relance (inscrits déf., contacts non relançables)`
              : ''}
            ). Le canal (entrant/sortant) est affiché s'il est renseigné.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
